import type { UserPermissionInfo } from 'ts/base/hooks/PermissionInfoHook';
import { useUserPermissionInfo } from 'ts/base/hooks/PermissionInfoHook';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { useUserInfo } from 'ts/base/hooks/UserInfoHook';
import { ArrayUtils } from 'ts/commons/ArrayUtils';
import { useProjectOption } from 'ts/commons/hooks/UseProjectOption';
import { useServerOption } from 'ts/commons/hooks/UseServerOption';
import { StringUtils } from 'ts/commons/StringUtils';
import { isPending } from 'ts/perspectives/findings/details/FlaggedFindingUtils';
import type { BlacklistingOption } from 'typedefs/BlacklistingOption';
import { type EFindingBlacklistTypeEntry } from 'typedefs/EFindingBlacklistType';
import { EProjectPermission } from 'typedefs/EProjectPermission';
import type { FindingBlacklistInfo } from 'typedefs/FindingBlacklistInfo';
import type { FindingExclusionApprovalOption } from 'typedefs/FindingExclusionApprovalOption';
import { EProjectOptions } from 'typedefs/ProjectOptions';
import { EServerOptions } from 'typedefs/ServerOptions';
import type { TrackedFinding } from 'typedefs/TrackedFinding';

/** Possible permissions the user can have for one/multiple findings */
export type FlaggingPermission = 'Suggest' | 'Approve' | 'Mark' | 'Unflag';

type BlacklistPermissions = {
	canFlagAsToleration: boolean;
	canFlagAsFalsePositive: boolean;
	disableEmptyRationale: boolean;
	detailedPermissions: Record<EFindingBlacklistTypeEntry, FlaggingPermission[]>;
};

function calculateDetailedPermissions(
	findings: TrackedFinding[],
	blacklistInformation: Array<FindingBlacklistInfo | null> | undefined,
	username: string,
	approvalWorkflowConfiguration: FindingExclusionApprovalOption,
	permissionInfo: UserPermissionInfo,
	projectId: string,
	blacklistingOption: BlacklistingOption
): Record<EFindingBlacklistTypeEntry, FlaggingPermission[]> {
	let permissions: Record<EFindingBlacklistTypeEntry, FlaggingPermission[]> | undefined = undefined;
	for (let i = 0; i < findings.length; i++) {
		const finding = findings[i]!;
		const findingBlacklistInformation = blacklistInformation?.[i];
		const findingPermissions = calculateDetailedFindingPermissions(
			username,
			finding,
			findingBlacklistInformation ?? undefined,
			approvalWorkflowConfiguration,
			permissionInfo,
			projectId,
			blacklistingOption
		);
		if (permissions === undefined) {
			permissions = findingPermissions;
		} else {
			permissions = {
				TOLERATION: ArrayUtils.intersection(permissions.TOLERATION, findingPermissions.TOLERATION),
				FALSE_POSITIVE: ArrayUtils.intersection(permissions.FALSE_POSITIVE, findingPermissions.FALSE_POSITIVE)
			};
		}
	}
	if (permissions === undefined) {
		permissions = {
			TOLERATION: [],
			FALSE_POSITIVE: []
		};
	}
	return permissions;
}

/** Returns the blacklist permissions as tuple of allowance to tolerate or to declare false positive */
export function useBlacklistPermissions(
	findings: TrackedFinding[],
	blacklistInformation?: Array<FindingBlacklistInfo | null>
): BlacklistPermissions {
	const username = useUserInfo().currentUser.username;
	const permissionInfo = useUserPermissionInfo();
	const projectId = useProjectId();
	const { data: approvalWorkflowConfiguration } = useProjectOption(EProjectOptions.FINDING_EXCLUSION_APPROVAL);
	const { data: blacklistingOption } = useServerOption(EServerOptions.BLACKLISTING);
	if (blacklistInformation !== undefined && blacklistInformation.length !== findings.length) {
		throw new Error('Incorrect number of flagging information provided');
	}
	if (approvalWorkflowConfiguration === undefined || blacklistingOption === undefined) {
		return {
			disableEmptyRationale: false,
			canFlagAsToleration: false,
			canFlagAsFalsePositive: false,
			detailedPermissions: {
				TOLERATION: [],
				FALSE_POSITIVE: []
			}
		};
	}
	const detailedPermissions = calculateDetailedPermissions(
		findings,
		blacklistInformation,
		username,
		approvalWorkflowConfiguration,
		permissionInfo,
		projectId,
		blacklistingOption
	);
	return {
		disableEmptyRationale: blacklistingOption.disableEmptyRationale,
		canFlagAsToleration: canEdit(detailedPermissions.TOLERATION),
		canFlagAsFalsePositive: canEdit(detailedPermissions.FALSE_POSITIVE),
		detailedPermissions
	};
}

function canEdit(permissions: FlaggingPermission[]) {
	return permissions.includes('Suggest') || permissions.includes('Mark');
}

function requiresApprovalWorkflow(
	finding: TrackedFinding,
	approvalWorkflowConfiguration: FindingExclusionApprovalOption
) {
	return (
		((finding.assessment === 'RED' && approvalWorkflowConfiguration.workflowEnabledRedFindings) ||
			(finding.assessment === 'YELLOW' && approvalWorkflowConfiguration.workflowEnabledYellowFindings)) &&
		(StringUtils.isEmptyOrWhitespace(approvalWorkflowConfiguration.findingCategories) ||
			approvalWorkflowConfiguration.findingCategories
				.split(',')
				.map(s => s.trim())
				.includes(finding.categoryName))
	);
}

function calculateDetailedFindingPermissions(
	username: string,
	finding: TrackedFinding,
	findingBlacklistInformation: FindingBlacklistInfo | undefined,
	approvalWorkflowConfiguration: FindingExclusionApprovalOption,
	permissionInfo: UserPermissionInfo,
	projectId: string,
	blacklistingOption: BlacklistingOption
): Record<EFindingBlacklistTypeEntry, FlaggingPermission[]> {
	const mustUseApprovalWorkflow = requiresApprovalWorkflow(finding, approvalWorkflowConfiguration);
	const canSuggest =
		permissionInfo.hasProjectPermission(projectId, EProjectPermission.EXCLUDE_RED_FINDINGS) ||
		(finding.assessment === 'YELLOW' &&
			permissionInfo.hasProjectPermission(projectId, EProjectPermission.EXCLUDE_YELLOW_FINDINGS));

	const canApprove =
		permissionInfo.hasProjectPermission(projectId, EProjectPermission.APPROVE_RED_FINDINGS_EXCLUSION) ||
		(finding.assessment === 'YELLOW' &&
			permissionInfo.hasProjectPermission(projectId, EProjectPermission.APPROVE_YELLOW_FINDINGS_EXCLUSION));
	const permissions: Set<FlaggingPermission> = new Set<FlaggingPermission>();

	if (mustUseApprovalWorkflow) {
		if (canSuggest) {
			permissions.add('Suggest');
			permissions.add('Unflag');
		}
		if (
			canApprove &&
			findingBlacklistInformation !== undefined &&
			isPending(findingBlacklistInformation.approvalState) &&
			(findingBlacklistInformation.user !== username || approvalWorkflowConfiguration.allowSelfApproval)
		) {
			permissions.add('Approve');
		}
		if (canSuggest && canApprove && approvalWorkflowConfiguration.allowSelfApproval) {
			permissions.add('Mark');
		}
	} else if (canSuggest) {
		permissions.add('Mark');
		permissions.add('Unflag');
	}
	return {
		TOLERATION: isTolerationDisabledForFinding(blacklistingOption, finding) ? [] : [...permissions],
		FALSE_POSITIVE: isFalsePositiveDisabledForFinding(blacklistingOption, finding) ? [] : [...permissions]
	};
}

function isTolerationDisabledForFinding(blacklistingOption: BlacklistingOption, finding: TrackedFinding): boolean {
	if (!blacklistingOption.disableTolerations) {
		return false;
	}
	return isQualityIndicatorDisabled(blacklistingOption.disableTolerationsForQualityIndicators, finding.categoryName);
}

function isFalsePositiveDisabledForFinding(blacklistingOption: BlacklistingOption, finding: TrackedFinding): boolean {
	if (!blacklistingOption.disableFalsePositives) {
		return false;
	}
	return isQualityIndicatorDisabled(
		blacklistingOption.disableFalsePositivesForQualityIndicators,
		finding.categoryName
	);
}

function isQualityIndicatorDisabled(disabledQualityIndicators: string, qualityIndicatorName: string): boolean {
	if (StringUtils.isEmptyOrWhitespace(disabledQualityIndicators)) {
		return true;
	}
	return disabledQualityIndicators
		.split(',')
		.map(name => name.trim())
		.filter(name => name.length > 0)
		.includes(qualityIndicatorName);
}
